import React from "react";
import LeadLink from "web/react/components/lead-link/lead-link";
import { PRODUCT_QUICK_VIEW_OVERLAY_NAME } from "web/react/components/product-quick-view/product-quick-view";
import { useProductClick } from "web/react/hooks/use-product-click/use-product-click";
import environment from "web/script/modules/environment";
import history from "web/script/utils/history";
import { useProductCardContext } from "./product-card";

interface ProductCardLinkProps {
    reason: string;
    predicate?: boolean;
    className?: string;
    children?: any;
    feedType?: string;
    onClick?: () => void;
    onMouseEnter?: () => void;
    onMouseLeave?: () => void;
}

function ProductCardLink({
    children,
    reason,
    className,
    predicate,
    onClick,
    feedType,
    onMouseEnter,
    onMouseLeave,
}: ProductCardLinkProps): React.ReactElement {
    const {
        id,
        uid,
        url,
        slug,
        image_url: imageURL,
        in_stock: inStock,
        link_id: linkId,
        lead_url: leadURL,
        affiliate_promo_url: affiliatePromoURL,
        context_type: contextType,
        overlay_enabled: overlayEnabled,
    } = useProductCardContext();

    const quickViewInfoFeatureEnabled =
        !!environment.getFeature("mg_quick_view_info") &&
        (!feedType || feedType === "DESIGNER_PROFILE");

    const predicateOrCurated =
        predicate || contextType === "promoted_products" || contextType === "collection_products";

    const { onClick: onProductClick } = useProductClick({
        id: id.toString(),
        productUrl: url,
        productImageUrl: imageURL || undefined,
        inStock,
        shouldRedirect: false,
        overlayEnabled: overlayEnabled,
        productUid: uid,
        productSlug: slug,
        reason: contextType ? `${contextType}-${reason}` : undefined,
        linkId: linkId?.toString(),
        href: url,
        isLeadLink: predicateOrCurated,
        openInNewTab: predicateOrCurated,
        leadURL: leadURL || undefined,
    });

    function handleLeadLinkClick(callback, event): void {
        onClick?.();
        onProductClick(event);
        callback(event);
    }

    function handleClick(event): void {
        if (quickViewInfoFeatureEnabled) {
            event.preventDefault();
            history.pushOverlay(PRODUCT_QUICK_VIEW_OVERLAY_NAME, {
                data: {
                    linkId: linkId?.toString(),
                },
            });
            return;
        }
        onClick?.();
        onProductClick(event);
    }

    if (predicateOrCurated) {
        return (
            <LeadLink
                href={leadURL as string}
                productId={id.toString()}
                openInNewTab={true}
                affiliatePromoUrl={affiliatePromoURL || undefined}
                reason={contextType ? `${contextType}-${reason}` : undefined}
                className={className}
                rel="nofollow"
                beforeOnClick={handleLeadLinkClick}
                onMouseEnter={onMouseEnter}
                onMouseLeave={onMouseLeave}
            >
                {children}
            </LeadLink>
        );
    }

    return (
        <a
            href={url}
            className={className}
            onClick={handleClick}
            onMouseEnter={onMouseEnter}
            onMouseLeave={onMouseLeave}
        >
            {children}
        </a>
    );
}

export default ProductCardLink;
export { ProductCardLinkProps };
